/**
 * Promotions — bonus-engine driven (Phase 2, 2026-02).
 *
 * Structure:
 *   1. Active bonuses grid (from `GET /api/promotions`)
 *      • `deposit_match`     → Deposit-CTA button (routes to /wallet/deposit)
 *      • `game_free_credit`  → Claim button (opens promo-code modal if required)
 *      • `cashback`          → info-only, "Auto-credited every {daily|weekly}"
 *   2. My claims section (from `GET /api/bonuses/mine`) — active first
 *      with wagering progress bar, historical below.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, Ticket, Repeat, RefreshCw, Sparkles, X } from "lucide-react";
import { Toaster, toast } from "sonner";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import AppShell from "../components/ui-r1/AppShell";
import Button from "../components/ui-r1/Button";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";
import ChatWidget from "../components/ChatWidget";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { CURRENCIES, formatAmount } from "../lib/currency";
import {
  BONUS_TYPE_LABEL,
  BONUS_TYPE_TONE,
  CLAIM_STATUS_LABEL,
  CLAIM_STATUS_TONE,
  currencyChipLabels,
  isActiveStatus,
  progressPct,
} from "../lib/bonusHelpers";

const ICON_FOR_TYPE = {
  deposit_match: Gift,
  game_free_credit: Ticket,
  cashback: Repeat,
};

export default function Promotions() {
  const { user, refresh } = useAuth();
  const { activeCurrency } = useCurrency();
  const [promos, setPromos] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claimTarget, setClaimTarget] = useState(null); // bonus opened in claim modal
  const [busyClaim, setBusyClaim] = useState(false);
  const [busyAbandon, setBusyAbandon] = useState(null); // claim_id being abandoned
  const nav = useNavigate();

  const reload = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        api.get("/promotions"),
        api.get("/bonuses/mine"),
      ]);
      setPromos(p.data?.items || []);
      setClaims(m.data?.items || []);
      setError(null);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeClaims = useMemo(() => claims.filter((c) => isActiveStatus(c.status)), [claims]);
  const historicalClaims = useMemo(() => claims.filter((c) => !isActiveStatus(c.status)), [claims]);

  // Hide bonuses the caller has already burned when `one_claim_per_user`
  // is set on the bonus. The backend returns them for admin-preview parity
  // but the CTA would 409 the second time — so we filter client-side by
  // matching on `bonus_slug` across the caller's entire claim history.
  const claimedSlugs = useMemo(
    () => new Set(claims.map((c) => c.bonus_slug).filter(Boolean)),
    [claims]
  );
  const visiblePromos = useMemo(
    () => promos.filter((b) => !(b.one_claim_per_user && claimedSlugs.has(b.slug))),
    [promos, claimedSlugs]
  );

  const handleDepositCTA = (bonus) => {
    const cur = (bonus.currencies || []).includes(activeCurrency) ? activeCurrency : (bonus.currencies?.[0] || activeCurrency);
    nav(`/wallet/deposit?currency=${cur}`);
  };

  const handleClaimSubmit = async (bonus, currency, promoCode) => {
    setBusyClaim(true);
    try {
      const { data } = await api.post("/bonuses/claim", {
        bonus_slug: bonus.slug,
        currency,
        promo_code: promoCode || null,
      });
      toast.success(`Claimed! ${formatAmount(data.amount_awarded, currency)} added to bonus balance.`);
      setClaimTarget(null);
      await reload();
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusyClaim(false);
    }
  };

  const handleAbandon = async (claim) => {
    // Show the *remaining* wagering & the bonus balance that will be zeroed.
    // Prior copy used `amount_awarded` conditionally which showed ৳0.00 once
    // any progress happened — misleading.
    const done = Number(claim.wagering_completed || 0);
    const req = Number(claim.wagering_required || 0);
    const remaining = Math.max(0, req - done);
    const line = req > 0
      ? `Wagering progress: ${formatAmount(done, claim.currency)} / ${formatAmount(req, claim.currency)} (${formatAmount(remaining, claim.currency)} left).`
      : "No wagering was required on this claim.";
    if (!window.confirm(
      `Abandon this bonus in ${claim.currency}? Your bonus balance will be forfeited; real balance is untouched.\n\n${line}`
    )) return;
    setBusyAbandon(claim.claim_id);
    try {
      await api.post(`/bonuses/claims/${claim.claim_id}/abandon`);
      toast.success("Bonus abandoned. Real balance untouched.");
      await reload();
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusyAbandon(null);
    }
  };

  return (
    <AppShell activeSection="promo" pageTitle="Promotions">
      <Toaster theme="dark" position="top-center" />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-6 space-y-10" data-testid="promotions-page">
        {/* Hero */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-2">Rewards</div>
            <h1 className="font-display italic text-3xl sm:text-5xl text-ink leading-tight">Promotions.</h1>
            <p className="text-sm sm:text-base text-ink-2 mt-3 max-w-xl">
              Deposit matches, cashback, free credits. All bonuses are wagered to real balance —
              you keep every win once you clear the requirement.
            </p>
          </div>
          <button
            onClick={reload}
            className="hidden sm:flex items-center gap-2 px-3 py-2 text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink border border-line rounded-lg"
            data-testid="promo-refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2" data-testid="promo-error">
            {error}
          </div>
        )}

        {/* Active bonuses */}
        <section data-testid="promo-active-list">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-3">Live offers</div>
          {loading && visiblePromos.length === 0 ? (
            <SkeletonGrid />
          ) : visiblePromos.length === 0 ? (
            <EmptyOffers />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visiblePromos.map((b) => (
                <BonusCard
                  key={b.slug}
                  bonus={b}
                  onDeposit={() => handleDepositCTA(b)}
                  onClaim={() => setClaimTarget(b)}
                  testid={`promo-card-${b.slug}`}
                />
              ))}
            </div>
          )}
        </section>

        {/* My claims */}
        {user && (
          <section data-testid="promo-my-claims">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-3">My claims</div>
            {activeClaims.length === 0 && historicalClaims.length === 0 ? (
              <Card>
                <CardBody className="text-sm text-ink-3 italic text-center py-8">
                  No bonuses yet. Deposit or claim one above to get started.
                </CardBody>
              </Card>
            ) : (
              <div className="space-y-3">
                {activeClaims.map((c) => (
                  <ActiveClaimRow
                    key={c.claim_id}
                    claim={c}
                    onAbandon={() => handleAbandon(c)}
                    busy={busyAbandon === c.claim_id}
                  />
                ))}
                {historicalClaims.length > 0 && (
                  <div className="pt-4 border-t border-line">
                    <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-2">History</div>
                    <div className="space-y-2">
                      {historicalClaims.map((c) => (
                        <HistoricalClaimRow key={c.claim_id} claim={c} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {claimTarget && (
        <ClaimModal
          bonus={claimTarget}
          onClose={() => setClaimTarget(null)}
          onSubmit={handleClaimSubmit}
          busy={busyClaim}
          activeCurrency={activeCurrency}
        />
      )}

      <ChatWidget />
    </AppShell>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-xl border border-line bg-elevated h-56 animate-pulse" />
      ))}
    </div>
  );
}

function EmptyOffers() {
  return (
    <Card>
      <CardBody className="text-center py-10">
        <Sparkles className="w-8 h-8 text-ink-3 mx-auto mb-3" />
        <div className="font-display italic text-lg text-ink mb-1">No offers live right now</div>
        <div className="text-xs text-ink-2">Fresh bonuses drop weekly. Check back soon.</div>
      </CardBody>
    </Card>
  );
}

function BonusCard({ bonus, onDeposit, onClaim, testid }) {
  const Icon = ICON_FOR_TYPE[bonus.bonus_type] || Gift;
  const chips = currencyChipLabels(bonus.currencies);
  const isDepositMatch = bonus.bonus_type === "deposit_match";
  const isFreeCredit = bonus.bonus_type === "game_free_credit";
  const isCashback = bonus.bonus_type === "cashback";
  return (
    <Card variant="glow" className="flex flex-col h-full" data-testid={testid}>
      <CardBody className="flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink/40 to-purple/20 border border-line flex items-center justify-center text-pink">
            <Icon className="w-5 h-5" />
          </span>
          <Badge variant={BONUS_TYPE_TONE[bonus.bonus_type] || "muted"}>
            {BONUS_TYPE_LABEL[bonus.bonus_type] || bonus.bonus_type}
          </Badge>
        </div>
        <div className="font-display italic text-2xl text-ink mb-1" data-testid={`${testid}-name`}>
          {bonus.name}
        </div>
        <div className="text-xs text-ink-2 leading-relaxed mb-4 flex-1">
          {bonus.description}
        </div>
        <div className="flex flex-wrap gap-1 mb-4">
          {chips.map((c) => (
            <span
              key={c}
              className="text-[9px] tracking-widest uppercase text-gold border border-gold/40 rounded px-1.5 py-0.5 bg-gold/5"
              data-testid={`${testid}-currency-${c}`}
            >
              {c}
            </span>
          ))}
          {bonus.promo_code_required && (
            <span className="text-[9px] tracking-widest uppercase text-pink border border-pink/50 rounded px-1.5 py-0.5 bg-pink/5">
              Promo code
            </span>
          )}
        </div>
        {isDepositMatch && (
          <Button variant="gold" size="md" className="w-full" onClick={onDeposit} data-testid={`${testid}-deposit-cta`}>
            Deposit to claim
          </Button>
        )}
        {isFreeCredit && (
          <Button variant="pink" size="md" className="w-full" onClick={onClaim} data-testid={`${testid}-claim-cta`}>
            Claim now
          </Button>
        )}
        {isCashback && (
          <div className="text-[10px] tracking-widest uppercase text-ink-3 text-center py-2 border border-dashed border-line rounded-lg" data-testid={`${testid}-cashback-info`}>
            Auto-credited every cycle
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ActiveClaimRow({ claim, onAbandon, busy }) {
  const pct = progressPct(claim);
  const done = Number(claim.wagering_completed || 0);
  const req = Number(claim.wagering_required || 0);
  return (
    <Card data-testid={`claim-active-${claim.claim_id}`}>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display italic text-lg text-ink truncate">{claim.bonus_name}</div>
              <Badge variant={CLAIM_STATUS_TONE[claim.status] || "muted"}>{CLAIM_STATUS_LABEL[claim.status] || claim.status}</Badge>
            </div>
            <div className="text-[11px] text-ink-3 mt-1">
              {BONUS_TYPE_LABEL[claim.bonus_type]} · {claim.currency} · awarded {formatAmount(claim.amount_awarded, claim.currency)}
            </div>
          </div>
          <button
            onClick={onAbandon}
            disabled={busy}
            className="text-[10px] tracking-widest uppercase text-ink-3 hover:text-danger transition disabled:opacity-40"
            data-testid={`claim-abandon-${claim.claim_id}`}
          >
            {busy ? "Working…" : "Abandon"}
          </button>
        </div>
        {req > 0 && (
          <div>
            <div className="flex items-center justify-between text-[11px] mb-1.5">
              <div className="tracking-widest uppercase text-ink-2">Wagering</div>
              <div className="text-gold font-mono" data-testid={`claim-progress-${claim.claim_id}`}>
                {formatAmount(done, claim.currency)} / {formatAmount(req, claim.currency)} · {pct}%
              </div>
            </div>
            <div className="h-1.5 bg-highest rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-pink to-gold transition-[width] duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            {claim.max_bet > 0 && (
              <div className="text-[10px] text-ink-3 mt-2">
                Max bet during wager: <span className="text-ink-2">{formatAmount(claim.max_bet, claim.currency)}</span>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function HistoricalClaimRow({ claim }) {
  return (
    <div className="rounded-lg border border-line bg-elevated px-3 py-2 flex items-center justify-between gap-3" data-testid={`claim-history-${claim.claim_id}`}>
      <div className="min-w-0">
        <div className="text-sm text-ink truncate">{claim.bonus_name}</div>
        <div className="text-[10px] text-ink-3">
          {claim.currency} · {formatAmount(claim.amount_awarded, claim.currency)}
          {claim.forfeited_reason && ` · ${claim.forfeited_reason}`}
        </div>
      </div>
      <Badge variant={CLAIM_STATUS_TONE[claim.status] || "muted"}>{CLAIM_STATUS_LABEL[claim.status]}</Badge>
    </div>
  );
}

function ClaimModal({ bonus, onClose, onSubmit, busy, activeCurrency }) {
  const eligibleCurrencies = bonus.currencies && bonus.currencies.length > 0
    ? bonus.currencies
    : CURRENCIES.map((c) => c.code);
  const initialCurrency = eligibleCurrencies.includes(activeCurrency) ? activeCurrency : eligibleCurrencies[0];
  const [currency, setCurrency] = useState(initialCurrency);
  const [promoCode, setPromoCode] = useState("");
  const requiresCode = !!bonus.promo_code_required;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="claim-modal">
      <div className="w-full max-w-md rounded-2xl border border-line bg-elevated p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-ink-3 hover:text-ink"
          data-testid="claim-modal-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-1">Claim</div>
        <h3 className="font-display italic text-2xl text-ink mb-2">{bonus.name}</h3>
        <p className="text-xs text-ink-2 mb-5 leading-relaxed">{bonus.description}</p>

        <div className="space-y-4">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-ink-3 mb-1.5">Currency</div>
            <div className="flex flex-wrap gap-2">
              {eligibleCurrencies.map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={
                    "px-3 py-1.5 rounded-md text-[10px] tracking-widest uppercase border transition " +
                    (currency === c
                      ? "border-pink text-pink bg-pink/10"
                      : "border-line text-ink-2 hover:border-ink-3")
                  }
                  data-testid={`claim-modal-currency-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          {requiresCode && (
            <div>
              <label className="text-[10px] tracking-widest uppercase text-ink-3 mb-1.5 block">Promo code</label>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                className="w-full bg-base border border-line rounded-lg px-3 py-2 text-sm text-ink focus:border-pink focus:outline-none"
                placeholder="Enter code"
                data-testid="claim-modal-promo-code"
              />
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <Button variant="ghost" size="md" className="flex-1" onClick={onClose} data-testid="claim-modal-cancel">
            Cancel
          </Button>
          <Button
            variant="pink"
            size="md"
            className="flex-1"
            loading={busy}
            onClick={() => onSubmit(bonus, currency, promoCode)}
            data-testid="claim-modal-submit"
          >
            Claim
          </Button>
        </div>
      </div>
    </div>
  );
}
